import type { AiMessageFile, AiWidget } from '@/types/ai';

const API_BASE = import.meta.env.VITE_API_URL || '';

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] ?? null;
}

function buildHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };

  const shopId = localStorage.getItem('current_shop_id');
  if (shopId) {
    headers['X-Shop-Id'] = shopId;
  }

  const xsrfToken = getCookie('XSRF-TOKEN');
  if (xsrfToken) {
    headers['X-XSRF-TOKEN'] = decodeURIComponent(xsrfToken);
  }

  return headers;
}

export type StreamError = Error & { code?: string; status?: number };

export interface StreamResult {
  content: string;
  widgets: AiWidget[];
  files: AiMessageFile[];
  /** Names of the tools executed during the stream (from tool_result events). */
  toolNames: string[];
}

export interface StreamCallbacks {
  /** Full accumulated content so far (called on every text delta). */
  onToken: (content: string) => void;
  /** Widgets captured so far (called whenever a new widget card is captured mid-stream). */
  onWidgets?: (widgets: AiWidget[]) => void;
  onDone: (result: StreamResult) => void;
  onError: (error: StreamError) => void;
}

export interface StreamOutcome {
  ok: boolean;
  /** Error code when ok is false (e.g. STREAMING_UNAVAILABLE → caller should fall back). */
  errorCode?: string;
  /** True when the failure happened after content started arriving (unsafe to re-send). */
  startedStreaming?: boolean;
}

// Mirror of the backend WidgetExtractionService mapping (WIDGET_TOOLS / MAX_WIDGETS).
const WIDGET_TOOLS: Record<string, AiWidget['type']> = {
  get_shop_health: 'shop_health',
  get_daily_summary: 'sales_summary',
  confirm_sale: 'receipt',
  compare_periods: 'period_comparison',
  get_stock_alerts: 'stock_alerts',
  get_top_products: 'top_products',
};

const MAX_WIDGETS = 6;

function captureWidget(toolName: string, rawResult: unknown, widgets: AiWidget[]): AiWidget | null {
  const type = WIDGET_TOOLS[toolName];
  if (!type || widgets.length >= MAX_WIDGETS) return null;

  let data: unknown = rawResult;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return null;
    }
  }

  // List-shaped results (stock alerts, top products) are wrapped as {items},
  // and an empty list produces no card — same rules as the backend capture.
  if (Array.isArray(data)) {
    if (data.length === 0) return null;
    data = { items: data };
  }

  if (data === null || typeof data !== 'object') return null;
  const record = data as Record<string, unknown>;
  if ('error' in record) return null;
  if (toolName === 'confirm_sale' && record.success !== true) return null;

  return { type, data: record } as unknown as AiWidget;
}

function captureFile(rawResult: unknown, files: AiMessageFile[]): AiMessageFile | null {
  let data: unknown = rawResult;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return null;
    }
  }

  if (data === null || typeof data !== 'object') return null;
  const record = data as Record<string, unknown>;
  if (record.success !== true) return null;
  if (typeof record.url !== 'string' || typeof record.filename !== 'string') return null;

  const file: AiMessageFile = {
    file_url: record.url,
    file_name: record.filename,
    file_type: typeof record.type === 'string' ? record.type : 'file',
  };
  if (files.some((f) => f.file_url === file.file_url)) return null;

  return file;
}

/**
 * Stream a chat message over the Laravel AI SDK SSE protocol.
 *
 * Each SSE `data:` line is a JSON event ({type: 'text_delta' | 'tool_result' |
 * 'error' | ...}). Text deltas build the assistant reply; tool_result events
 * are mined for rich widget cards and generated files, mirroring the backend
 * capture rules so cards appear live without waiting for the refetch.
 */
export async function streamChatMessage(
  conversationId: string,
  message: string,
  callbacks: StreamCallbacks,
): Promise<StreamOutcome> {
  const url = `${API_BASE}/api/v1/ai/chat/${conversationId}/messages/stream`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: buildHeaders(),
      body: JSON.stringify({ message }),
    });
  } catch {
    const err = new Error('Impossible de se connecter au serveur.') as StreamError;
    err.code = 'NETWORK_ERROR';
    err.status = 0;
    callbacks.onError(err);
    return { ok: false, errorCode: err.code };
  }

  if (!response.ok) {
    let errorCode = 'AI_API_ERROR';
    let errorMessage = "Erreur lors de la communication avec l'assistant IA.";

    try {
      const errorBody = await response.json();
      errorCode = errorBody?.error?.code ?? errorCode;
      errorMessage = errorBody?.error?.message ?? errorMessage;
    } catch {
      // Could not parse error body
    }

    const err = new Error(errorMessage) as StreamError;
    err.code = errorCode;
    err.status = response.status;
    callbacks.onError(err);
    return { ok: false, errorCode };
  }

  const body = response.body;
  if (!body) {
    callbacks.onDone({ content: '', widgets: [], files: [], toolNames: [] });
    return { ok: true };
  }

  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let fullContent = '';
  const widgets: AiWidget[] = [];
  const files: AiMessageFile[] = [];
  const toolNames: string[] = [];
  let streamErrorMessage: string | null = null;

  const handleData = (data: string) => {
    if (data === '[DONE]') return;

    let event: unknown;
    try {
      event = JSON.parse(data);
    } catch {
      // Defensive: non-JSON payload — treat as a raw text chunk.
      fullContent += data;
      callbacks.onToken(fullContent);
      return;
    }

    if (event === null || typeof event !== 'object') return;
    const record = event as Record<string, unknown>;

    switch (record.type) {
      case 'text_delta':
        if (typeof record.delta === 'string' && record.delta !== '') {
          fullContent += record.delta;
          callbacks.onToken(fullContent);
        }
        break;
      case 'tool_result': {
        const toolName = typeof record.tool_name === 'string' ? record.tool_name : '';
        if (toolName !== '') {
          toolNames.push(toolName);
        }
        const widget = captureWidget(toolName, record.result, widgets);
        if (widget) {
          widgets.push(widget);
          callbacks.onWidgets?.([...widgets]);
        }
        const file = captureFile(record.result, files);
        if (file) {
          files.push(file);
        }
        break;
      }
      case 'error':
        if (typeof record.message === 'string' && record.message !== '') {
          streamErrorMessage = record.message;
        } else {
          streamErrorMessage = "Erreur lors de la communication avec l'assistant IA.";
        }
        break;
      default:
        // stream_start, text_start, text_end, tool_call, stream_end, … — ignored.
        break;
    }
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // SSE frames are newline-delimited; keep the trailing partial line buffered.
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        const trimmed = line.trimEnd();
        if (trimmed.startsWith('data: ')) {
          handleData(trimmed.slice(6));
        } else if (trimmed.startsWith('data:')) {
          handleData(trimmed.slice(5));
        }
      }
    }

    if (buffer.startsWith('data: ')) {
      handleData(buffer.trimEnd().slice(6));
    }
  } catch {
    // Stream interrupted — surface what we have; the server may still have
    // persisted the full message (refetch reconciles).
    if (fullContent === '') {
      const err = new Error('La connexion au flux a été interrompue.') as StreamError;
      err.code = 'STREAM_INTERRUPTED';
      callbacks.onError(err);
      return { ok: false, errorCode: err.code, startedStreaming: false };
    }
  }

  if (streamErrorMessage !== null && fullContent === '') {
    const err = new Error(streamErrorMessage) as StreamError;
    err.code = 'AI_API_ERROR';
    callbacks.onError(err);
    return { ok: false, errorCode: err.code, startedStreaming: false };
  }

  callbacks.onDone({ content: fullContent, widgets, files, toolNames });
  return { ok: true, startedStreaming: true };
}
